import type { Category, GroupDimension, Lane, PayloadSnapshot, SortOrder, TrendWindow, ViewState } from './types'

export const NO_CATEGORY = '__nocat__'

// URL-supplied free text is capped rather than rejected outright; anything longer just gets
// truncated in place of falling back to the default, since a long search term is still a
// meaningful value.
const MAX_TEXT_LENGTH = 200

const CATEGORY_VALUES: readonly Category[] = [
  'reliability', 'techhealth', 'security', 'observability', 'infraops', 'cost', 'delivery',
]

const GROUP_DIMENSIONS: readonly GroupDimension[] = ['project', 'cat', 'epic', 'assignee', 'svp', 'manager', 'app']

const SORT_ORDERS: readonly SortOrder[] = ['worst', 'score', 'overdue', 'idle', 'age', 'due', 'slips', 'entered', 'key']

const TREND_WINDOWS: readonly TrendWindow[] = ['30d', '60d', '90d', 'ytd', 'all']

const LANES: readonly Lane[] = ['intake', 'score', 'cat', 'own', 'redate', 'limbo']

const CAT_FIELD_VALUES: readonly ViewState['cat'][] = [...CATEGORY_VALUES, 'any', NO_CATEGORY]

const SCOPE_VALUES: readonly ViewState['scope'][] = ['open', 'closed', 'all']
const DUE_VALUES: readonly ViewState['due'][] = ['any', 'overdue', 'atrisk', 'soon', 'nodate']
const IDLE_VALUES: readonly ViewState['idle'][] = ['any', 'stale', 'stalled']
const SCORE_VALUES: readonly ViewState['score'][] = ['any', 'high', 'measured', 'unscored']
const OWNER_VALUES: readonly ViewState['owner'][] = ['any', 'unassigned']
const CATSRC_VALUES: readonly ViewState['catsrc'][] = ['any', 'field', 'missing']
const STAGE_VALUES: readonly ViewState['stage'][] = ['any', 'todo', 'prog', 'limbo']
const DUEMOVE_VALUES: readonly ViewState['duemove'][] = ['any', 'moved', 'slipped', 'repeat', 'never']
const RECENT_VALUES: readonly ViewState['recent'][] = ['14', '30', '60', '90']
const PRI_VALUES: readonly ViewState['pri'][] = ['any', 'p1', 'rest']
const EXPAND_VALUES: readonly ViewState['expand'][] = ['auto', 'all']

// The "since last session" window offered by ReviewQueuePanel; despite the name this is a
// day-count, not a date (the actual as-of date lives outside ViewState, see withAsOf).
const SINCE_VALUES = ['7', '14', '30']

type ViewStatePayload = Pick<PayloadSnapshot, 'since_default' | 'deliv_window' | 'svp_order' | 'deliv_windows'>

function isOneOf<T extends string>(values: readonly T[], value: string): value is T {
  return (values as readonly string[]).includes(value)
}

function isText(value: string): boolean {
  return value.length <= MAX_TEXT_LENGTH
}

export function defaults(payload: ViewStatePayload): ViewState {
  return {
    area: 'ALL', group: 'project', scope: 'open', due: 'any', idle: 'any', score: 'any',
    owner: 'any', cat: 'any', catsrc: 'any', stage: 'any', q: '', sort: 'worst', win: 'ytd',
    acct: 'project', unit: '', duemove: 'any', recent: '30', monthby: 'project', month: '',
    pri: 'any', lane: '', since: payload.since_default, expand: 'auto', laneall: '',
    dwin: payload.deliv_window, deliverby: 'project',
  }
}

/**
 * Validates one URL-supplied field against the field's actual allowed values and, if valid,
 * writes it onto `base`. Unknown or malformed values are left alone, so `base` keeps the
 * default already in place for that key.
 */
function applyField(base: ViewState, key: keyof ViewState, raw: string, payload: ViewStatePayload): void {
  switch (key) {
    case 'area':
      if (raw === 'ALL' || payload.svp_order.includes(raw)) base.area = raw
      return
    case 'group':
      if (isOneOf(GROUP_DIMENSIONS, raw)) base.group = raw
      return
    case 'scope':
      if (isOneOf(SCOPE_VALUES, raw)) base.scope = raw
      return
    case 'due':
      if (isOneOf(DUE_VALUES, raw)) base.due = raw
      return
    case 'idle':
      if (isOneOf(IDLE_VALUES, raw)) base.idle = raw
      return
    case 'score':
      if (isOneOf(SCORE_VALUES, raw)) base.score = raw
      return
    case 'owner':
      if (isOneOf(OWNER_VALUES, raw)) base.owner = raw
      return
    case 'cat':
      if (isOneOf(CAT_FIELD_VALUES, raw)) base.cat = raw
      return
    case 'catsrc':
      if (isOneOf(CATSRC_VALUES, raw)) base.catsrc = raw
      return
    case 'stage':
      if (isOneOf(STAGE_VALUES, raw)) base.stage = raw
      return
    case 'q':
      if (isText(raw)) base.q = raw
      return
    case 'sort':
      if (isOneOf(SORT_ORDERS, raw)) base.sort = raw
      return
    case 'win':
      if (isOneOf(TREND_WINDOWS, raw)) base.win = raw
      return
    case 'acct':
      if (isOneOf(GROUP_DIMENSIONS, raw)) base.acct = raw
      return
    case 'unit':
      if (isText(raw)) base.unit = raw
      return
    case 'duemove':
      if (isOneOf(DUEMOVE_VALUES, raw)) base.duemove = raw
      return
    case 'recent':
      if (isOneOf(RECENT_VALUES, raw)) base.recent = raw
      return
    case 'monthby':
      if (isOneOf(GROUP_DIMENSIONS, raw)) base.monthby = raw
      return
    case 'month':
      if (isText(raw)) base.month = raw
      return
    case 'pri':
      if (isOneOf(PRI_VALUES, raw)) base.pri = raw
      return
    case 'lane':
      if (raw === '' || isOneOf(LANES, raw)) base.lane = raw
      return
    case 'since':
      if (isOneOf(SINCE_VALUES, raw)) base.since = raw
      return
    case 'expand':
      if (isOneOf(EXPAND_VALUES, raw)) base.expand = raw
      return
    case 'laneall':
      if (raw === '' || isOneOf(LANES, raw)) base.laneall = raw
      return
    case 'dwin':
      if (payload.deliv_windows.map(String).includes(raw)) base.dwin = raw
      return
    case 'deliverby':
      if (isOneOf(GROUP_DIMENSIONS, raw)) base.deliverby = raw
      return
  }
}

export function decodeViewState(search: string, payload: ViewStatePayload): ViewState {
  const base = defaults(payload)
  const params = new URLSearchParams(search.replace(/^[?#]/, ''))
  for (const key of Object.keys(base) as Array<keyof ViewState>) {
    const raw = params.get(key)
    if (raw !== null) applyField(base, key, raw, payload)
  }
  if (base.cat !== 'any' && base.catsrc !== 'any') base.catsrc = 'any'
  return base
}

export function encodeViewState(state: ViewState, payload: ViewStatePayload): string {
  const base = defaults(payload)
  const params = new URLSearchParams()
  for (const key of Object.keys(base) as Array<keyof ViewState>) {
    if (state[key] !== base[key]) params.set(key, state[key])
  }
  return params.toString()
}

export function withAsOf(search: string, asOf: string | undefined): string {
  const params = new URLSearchParams(search.replace(/^[?#]/, ''))
  if (asOf) params.set('asOf', asOf)
  else params.delete('asOf')
  const nextSearch = params.toString()
  return nextSearch ? `?${nextSearch}` : ''
}

export function resetViewState(payload: ViewStatePayload): ViewState {
  return defaults(payload)
}
